import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import authService from "../../services/authService";
import { useAuth } from "../../contexts/AuthContext";
import ProfessionSelect, {
  ProfessionSelectExtras,
  toProfessions,
} from "../common/ProfessionSelect";
import {
  sanitizeAlphanumeric,
  sanitizeInput,
  validatePassword,
  validateUsername,
} from "../../utils/inputValidation";

// Icon components
const EyeIcon = ({ className = "" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    fill="none"
    viewBox="0 0 24 24"
    className={className}
    aria-hidden="true"
  >
    <path
      d="M1.5 12s3.5-7 10.5-7 10.5 7 10.5 7-3.5 7-10.5 7S1.5 12 1.5 12Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const EyeOffIcon = ({ className = "" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    fill="none"
    viewBox="0 0 24 24"
    className={className}
    aria-hidden="true"
  >
    <path
      d="M3 3l18 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M9.88 9.88A3.5 3.5 0 0 0 12 8.5c1.93 0 3.5 1.57 3.5 3.5 0 .75-.24 1.45-.65 2.02"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6.11 6.11C3.86 7.68 2.4 9.7 1.5 12c0 0 3.5 7 10.5 7 2.15 0 4-.47 5.61-1.26M14.12 14.12A3.5 3.5 0 0 1 12 15.5c-1.93 0-3.5-1.57-3.5-3.5 0-.46.09-.9.26-1.3"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M22.5 12s-1.05-2.13-3.21-3.98"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const InviteRegister = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { registerWithInvitation } = useAuth();

  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [invitationData, setInvitationData] = useState(null);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    professionType: "",
    additionalProfessions: [],
    practiceInstructorDepartment: "",
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const inviteRole = invitationData?.role;
  const isPracticeInstructorInvite = inviteRole === "PRACTICE_INSTRUCTOR";
  const isRevisorInvite = inviteRole === "REVISOR";
  const requiresProfessionType = !isRevisorInvite;
  // Instructors can supervise apprentices of several professions; learners cannot.
  const allowsMultipleProfessions =
    isPracticeInstructorInvite || inviteRole === "BERUFSBILDNER";
  const inviteRoleLabel =
    inviteRole === "BERUFSBILDNER"
      ? "Berufsbildner"
      : isPracticeInstructorInvite
        ? "Practice Instructor"
        : isRevisorInvite
          ? "Revisor"
          : "member";

  useEffect(() => {
    const validate = async () => {
      const result = await authService.validateInvitation(token);
      if (result.success) {
        setValid(true);
        setInvitationData({
          role: result.role,
          professionType: result.professionType,
        });
        if (result.professionType) {
          setFormData((prev) => ({
            ...prev,
            professionType: result.professionType,
          }));
        }
      } else {
        setValid(false);
        setErrorMsg(result.message || "Invalid or expired invitation link.");
      }
      setLoading(false);
    };
    validate();
  }, [token]);

  useEffect(() => {
    if (formData.firstName && formData.lastName) {
      const cleanLastName = formData.lastName.toLowerCase().replace(/\s+/g, "");
      const generatedUsername = cleanLastName;
      const cleanFirstName = formData.firstName
        .toLowerCase()
        .replace(/\s+/g, "");
      const generatedEmail = `${cleanFirstName}.${cleanLastName}@sunrise.net`;
      setFormData((prev) => ({
        ...prev,
        username: generatedUsername,
        email: generatedEmail,
      }));
    }
  }, [formData.firstName, formData.lastName]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;
    if (name === "firstName" || name === "lastName") {
      processedValue = sanitizeInput(value);
    } else if (name === "username") {
      processedValue = sanitizeAlphanumeric(value);
    } else if (name === "practiceInstructorDepartment") {
      processedValue = sanitizeInput(value);
    }
    setFormData((prev) => ({
      ...prev,
      [name]: processedValue,
    }));
    if (errors[name]) setErrors({ ...errors, [name]: "" });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim())
      newErrors.firstName = "First name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";

    const usernameValidation = validateUsername(formData.username);
    if (!usernameValidation.isValid) {
      newErrors.username = usernameValidation.error;
    }

    // Password check
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else {
      const passValidation = validatePassword(formData.password);
      if (!passValidation.isValid)
        newErrors.password = passValidation.errors[0];
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (
      requiresProfessionType &&
      !formData.professionType &&
      !invitationData.professionType
    ) {
      newErrors.professionType = "Please select a profession type";
    }

    if (
      isPracticeInstructorInvite &&
      !formData.practiceInstructorDepartment.trim()
    ) {
      newErrors.practiceInstructorDepartment =
        "Please enter the department or team";
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    setLoading(true);
    try {
      const result = await registerWithInvitation(token, {
        username: formData.username,
        firstName: formData.firstName,
        lastName: formData.lastName,
        professionType: requiresProfessionType
          ? formData.professionType || invitationData.professionType
          : null,
        professions:
          requiresProfessionType && allowsMultipleProfessions
            ? toProfessions(
                formData.professionType || invitationData.professionType,
                formData.additionalProfessions,
              )
            : undefined,
        practiceInstructorDepartment: isPracticeInstructorInvite
          ? formData.practiceInstructorDepartment
          : "",
        password: formData.password,
      });

      if (result.success) {
        navigate("/");
      } else {
        setErrors({ submit: result.message });
      }
    } catch (err) {
      setErrors({ submit: "Registration failed." });
    }
    setLoading(false);
  };

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-green-50">
        <div className="animate-spin h-8 w-8 text-green-600 border-4 border-green-600 rounded-full border-t-transparent"></div>
      </div>
    );

  if (!valid)
    return (
      <div className="min-h-screen flex items-center justify-center bg-red-50 p-4">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full text-center">
          <div className="text-red-500 mb-4">
            <svg
              className="w-16 h-16 mx-auto"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">
            Invalid Invitation
          </h2>
          <p className="text-gray-600">{errorMsg}</p>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen bg-green-cloud flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
      <div className="max-w-md w-full space-y-8">
        <div className="card-brand p-8 animate-fade-in">
          <div className="text-center mb-8">
            <img
              src="/Journey_Logo.png"
              alt="Journey Logo"
              className="mx-auto mb-4 h-36 object-contain"
            />
            <h2 className="text-xl font-semibold text-grey-dusk mb-2">
              Complete your {inviteRoleLabel} account
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label-brand">First Name</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  className={`input-brand ${errors.firstName ? "input-brand-error" : ""}`}
                  placeholder="Enter your first name"
                />
                {errors.firstName && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.firstName}
                  </p>
                )}
              </div>

              <div>
                <label className="label-brand">Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className={`input-brand ${errors.lastName ? "input-brand-error" : ""}`}
                  placeholder="Enter your last name"
                />
                {errors.lastName && (
                  <p className="text-sm text-red-600 mt-1">{errors.lastName}</p>
                )}
              </div>
            </div>

            {/* Generated account data */}
            <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 space-y-3">
              <div>
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">
                  Username
                </span>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  className={`input-brand font-mono ${errors.username ? "input-brand-error" : ""}`}
                  placeholder="generated..."
                />
                {errors.username && (
                  <p className="text-sm text-red-600 mt-1">{errors.username}</p>
                )}
              </div>
              <div>
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">
                  Email (Preview)
                </span>
                <div className="font-mono text-gray-700 bg-white px-3 py-2 rounded border border-gray-200 break-all">
                  {formData.email || (
                    <span className="text-gray-300 italic">generated...</span>
                  )}
                </div>
              </div>
            </div>

            {/* Profession Type - Only if not preset */}
            {requiresProfessionType && !invitationData.professionType && (
              <ProfessionSelect
                label="Profession Type"
                value={formData.professionType}
                additional={formData.additionalProfessions}
                allowMultiple={allowsMultipleProfessions}
                error={errors.professionType}
                onChange={({ professionType, additional }) =>
                  setFormData((prev) => ({
                    ...prev,
                    professionType,
                    additionalProfessions: additional,
                  }))
                }
              />
            )}
            {requiresProfessionType && invitationData.professionType && (
              <div>
                <label className="label-brand">Profession Type</label>
                <input
                  type="text"
                  value={invitationData.professionType}
                  readOnly
                  className="input-brand bg-gray-50 text-gray-500 cursor-not-allowed"
                />
                {allowsMultipleProfessions && (
                  <ProfessionSelectExtras
                    primary={invitationData.professionType}
                    additional={formData.additionalProfessions}
                    onChange={(additional) =>
                      setFormData((prev) => ({
                        ...prev,
                        additionalProfessions: additional,
                      }))
                    }
                  />
                )}
              </div>
            )}

            {isPracticeInstructorInvite && (
              <div>
                <label className="label-brand">Department / Abteilung</label>
                <input
                  type="text"
                  name="practiceInstructorDepartment"
                  value={formData.practiceInstructorDepartment}
                  onChange={handleChange}
                  className={`input-brand ${errors.practiceInstructorDepartment ? "input-brand-error" : ""}`}
                  placeholder="Enter your department or team"
                />
                {errors.practiceInstructorDepartment && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.practiceInstructorDepartment}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="label-brand">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    autoComplete="new-password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`input-brand pr-12 ${errors.password ? "input-brand-error" : ""}`}
                    placeholder="Create a strong password"
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-black transition-colors"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-sm text-red-600 mt-1">{errors.password}</p>
                )}
              </div>

              <div>
                <label className="label-brand">Confirm Password</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    autoComplete="new-password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`input-brand pr-12 ${errors.confirmPassword ? "input-brand-error" : ""}`}
                    placeholder="Confirm your password"
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-black transition-colors"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.confirmPassword}
                  </p>
                )}
              </div>
            </div>

            {errors.submit && (
              <div
                className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative"
                role="alert"
              >
                <strong className="font-bold">Registration Failed: </strong>
                <span className="block sm:inline">{errors.submit}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className={`btn-brand-primary w-full shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-0.5 ${
                loading ? "opacity-75 cursor-not-allowed" : ""
              }`}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 0 1 4 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Creating Account...
                </div>
              ) : (
                "Create Account"
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default InviteRegister;
